import { useEffect, useMemo, useRef } from 'react';
import { campaignDefinedVariables, variableNameKey } from '../../../../utils/variableScope';
import { getSharedRowsRef, mergeLiveRows } from './sharedVariableRowsRef';

/** Scope flags, live row refs, and campaign overlays hidden behind This-doc. */
export default function useVariableTableRows({
  docVariables,
  onDocVariablesChange,
  onRenameVariable,
  historyEpoch,
  campaign,
  onCampaignVariablesChange,
  missionVariables = null,
  onMissionVariablesChange = null,
}) {
  const newRowIdRef = useRef(null);

  const hasDocScope = !!onDocVariablesChange;
  const hasCampaignScope = !!onCampaignVariablesChange && !!campaign;
  const hasMissionScope = typeof onMissionVariablesChange === 'function';
  const docRows = hasDocScope ? docVariables || [] : [];
  const campaignRows = hasCampaignScope ? campaign?.variables || [] : [];
  const docNameKeys = useMemo(
    () => new Set(docRows.map((v) => variableNameKey(v.name)).filter(Boolean)),
    [docRows]
  );
  // Campaign overlays of doc-declared names stay hidden — one "This doc" row
  // shows this campaign's value.
  const displayCampaignRows = useMemo(() => {
    const defined = campaignDefinedVariables(campaignRows);
    if (!hasDocScope) return defined;
    return defined.filter((v) => !docNameKeys.has(variableNameKey(v.name)));
  }, [hasDocScope, campaignRows, docNameKeys]);

  const missionRows = Array.isArray(missionVariables) ? missionVariables : [];
  const localDocRowsRef = useRef(docRows);
  const localCampaignRowsRef = useRef(campaignRows);
  const localMissionRowsRef = useRef(missionRows);
  // Main table + every linked-variable-row mount a VariablesTable. Share one
  // live list per host writer so a patch in one instance cannot rewrite the
  // others back to empty (number fields look like they flipped to 0).
  const docRowsRef =
    getSharedRowsRef(onDocVariablesChange, docRows) || localDocRowsRef;
  const campaignRowsRef =
    getSharedRowsRef(onCampaignVariablesChange, campaignRows) ||
    localCampaignRowsRef;
  const missionRowsRef =
    getSharedRowsRef(onMissionVariablesChange, missionRows) ||
    localMissionRowsRef;
  const editableMembership = useMemo(
    () =>
      `${docRows.map((v) => v.id).join(',')}|${campaignRows.map((v) => v.id).join(',')}|${missionRows.map((v) => v.id).join(',')}`,
    [docRows, campaignRows, missionRows]
  );
  const prevEpochRef = useRef(historyEpoch);
  useEffect(() => {
    const undoRestore = prevEpochRef.current !== historyEpoch;
    prevEpochRef.current = historyEpoch;
    if (undoRestore) {
      docRowsRef.current = docRows;
      campaignRowsRef.current = campaignRows;
      missionRowsRef.current = missionRows;
      return;
    }
    docRowsRef.current = mergeLiveRows(docRowsRef.current, docRows);
    campaignRowsRef.current = mergeLiveRows(
      campaignRowsRef.current,
      campaignRows
    );
    missionRowsRef.current = mergeLiveRows(missionRowsRef.current, missionRows);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editableMembership, historyEpoch]);

  const onDocRef = useRef(onDocVariablesChange);
  const onCampRef = useRef(onCampaignVariablesChange);
  const onMissionRef = useRef(onMissionVariablesChange);
  const onRenameVariableRef = useRef(onRenameVariable);
  onDocRef.current = onDocVariablesChange;
  onCampRef.current = onCampaignVariablesChange;
  onMissionRef.current = onMissionVariablesChange;
  onRenameVariableRef.current = onRenameVariable;

  return {
    newRowIdRef,
    hasDocScope,
    hasCampaignScope,
    hasMissionScope,
    docRows,
    campaignRows,
    displayCampaignRows,
    docRowsRef,
    campaignRowsRef,
    missionRows,
    missionRowsRef,
    onDocRef,
    onCampRef,
    onMissionRef,
    onRenameVariableRef,
  };
}
